import type { PaletteMode } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { PaletteOptions } from '@mui/material/styles';

import {
  ACTION_ALPHA,
  BACKGROUND,
  BRAND_COLORS,
  COMMON,
  COR_DEFAULT,
  CorNomeada,
  DIVIDER_ALPHA,
  GREY_SCALE,
  OVERLAY_WHITE,
  SEMANTIC_ROLES,
  SIDEBAR_OVERLAY_ALPHA,
  TEXT_ALPHA,
} from './tokens';

export interface SidebarPalette {
  hover: string;
  selected: string;
  selectedHover: string;
  border: string;
  skeleton: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    sidebar: SidebarPalette;
  }
  interface PaletteOptions {
    sidebar?: SidebarPalette;
  }
}

export interface PaletteInput {
  mode?: PaletteMode;
  cor?: string | null;
}

/** Nome de cor normalizado; `azul` quando ausente, vazio ou desconhecido. */
export function resolveCorName(cor: string | null): CorNomeada {
  const normalized = (cor ?? '').trim().toLowerCase();
  // hasOwnProperty evita que chaves herdadas do Object.prototype ("constructor",
  // "toString", "__proto__") sejam lidas como se fossem cores válidas.
  return Object.prototype.hasOwnProperty.call(BRAND_COLORS, normalized)
    ? (normalized as CorNomeada)
    : COR_DEFAULT;
}

// Os tons de hover/selected que o MUI deriva de `primary` pressupõem fundo claro e
// somem sobre a sidebar, cujo fundo já é a própria primária. O overlay em branco é
// o que dá contraste, e por isso não varia com a cor da conta.
function buildSidebarPalette(): SidebarPalette {
  return {
    hover: alpha(OVERLAY_WHITE, SIDEBAR_OVERLAY_ALPHA.hover),
    selected: alpha(OVERLAY_WHITE, SIDEBAR_OVERLAY_ALPHA.selected),
    selectedHover: alpha(OVERLAY_WHITE, SIDEBAR_OVERLAY_ALPHA.selectedHover),
    border: alpha(OVERLAY_WHITE, SIDEBAR_OVERLAY_ALPHA.border),
    skeleton: alpha(OVERLAY_WHITE, SIDEBAR_OVERLAY_ALPHA.skeleton),
  };
}

// Sem parâmetro, como buildSidebarPalette(): a invariância em relação à cor da conta
// é estrutural, não uma coincidência de valores a testar caso a caso.
function buildNeutralPalette(): PaletteOptions {
  const { black } = COMMON;
  return {
    common: { ...COMMON },
    grey: { ...GREY_SCALE },
    background: { ...BACKGROUND },
    text: {
      primary: alpha(black, TEXT_ALPHA.primary),
      secondary: alpha(black, TEXT_ALPHA.secondary),
      disabled: alpha(black, TEXT_ALPHA.disabled),
    },
    divider: alpha(black, DIVIDER_ALPHA),
    // Cor e coeficiente de cada estado saem do mesmo número: o deepmerge do MUI não
    // recalcula um a partir do outro, e declarar só um deixaria os dois divergindo.
    action: {
      active: alpha(black, ACTION_ALPHA.active),
      hover: alpha(black, ACTION_ALPHA.hover),
      hoverOpacity: ACTION_ALPHA.hover,
      selected: alpha(black, ACTION_ALPHA.selected),
      selectedOpacity: ACTION_ALPHA.selected,
      disabled: alpha(black, ACTION_ALPHA.disabled),
      disabledBackground: alpha(black, ACTION_ALPHA.disabledBackground),
      disabledOpacity: ACTION_ALPHA.disabledOpacity,
      focus: alpha(black, ACTION_ALPHA.focus),
      focusOpacity: ACTION_ALPHA.focus,
      activatedOpacity: ACTION_ALPHA.activatedOpacity,
    },
  };
}

export function buildPalette({ mode, cor }: PaletteInput = {}): PaletteOptions {
  return {
    mode: mode ?? 'light',
    primary: { ...BRAND_COLORS[resolveCorName(cor ?? null)] },
    secondary: { main: SEMANTIC_ROLES.secondary },
    error: { main: SEMANTIC_ROLES.error },
    warning: { main: SEMANTIC_ROLES.warning },
    info: { main: SEMANTIC_ROLES.info },
    success: { main: SEMANTIC_ROLES.success },
    ...buildNeutralPalette(),
    sidebar: buildSidebarPalette(),
  };
}
